import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Siren, LayoutGrid, Briefcase } from 'lucide-react';
import { ZijMenu, ZijMenuMobielKnop, PaginaKop, type ZijMenuItem } from './ZijMenu';
import { werkplekApi } from '../services/professionalWerkplekApi';
import { initSocket } from '../services/socketService';

/**
 * Hoofdmenu Dashboard van de professional: exact drie banen in de linkerkolom
 * (spiegel van de opdrachtgever). Opdrachten is geen los hoofditem meer.
 */
export const DASHBOARD_PAD = '/professional/dashboard';
export const DASHBOARD_ITEMS = [
  { key: 'actiepunten', href: `${DASHBOARD_PAD}/actiepunten`, icon: Siren },
  { key: 'signalen', href: `${DASHBOARD_PAD}/signalen`, icon: LayoutGrid },
  { key: 'opdrachten', href: `${DASHBOARD_PAD}/opdrachten`, icon: Briefcase },
] as const;

export const WERKPLEK_SIGNALEN_KEY = ['professional-werkplek-signalen'];

export const ProfessionalDashboardSchil: React.FC<{
  titel?: string;
  ondertitel?: string;
  acties?: React.ReactNode;
  children: React.ReactNode;
}> = ({ titel, ondertitel, acties, children }) => {
  const { t } = useTranslation('professional');
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  // Tellers in het menu; dezelfde query als de signalenpagina, dus geen dubbele call.
  const { data } = useQuery({
    queryKey: WERKPLEK_SIGNALEN_KEY,
    queryFn: werkplekApi.signalen,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const socket = initSocket();
    if (!socket) return;
    const ververs = () => queryClient.invalidateQueries({ queryKey: WERKPLEK_SIGNALEN_KEY });
    socket.on('new_notification', ververs);
    socket.on('new_message', ververs);
    return () => {
      socket.off('new_notification', ververs);
      socket.off('new_message', ververs);
    };
  }, [queryClient]);

  const actiepunten = data?.signalen.filter((s) => s.source === 'afgeleid' && s.severity !== 'info').length ?? 0;
  const aantal: Record<string, number> = { actiepunten, signalen: data?.ongelezen ?? 0, opdrachten: 0 };

  const items: ZijMenuItem[] = DASHBOARD_ITEMS.map((item) => ({
    key: item.key,
    label: t(`werkplek.menu.${item.key}`),
    omschrijving: t(`werkplek.menu.${item.key}Desc`),
    icon: item.icon,
    href: item.href,
    actief: pathname.startsWith(item.href),
    aantal: aantal[item.key],
  }));

  return (
    <div className="bg-gray-50">
      <ZijMenuMobielKnop onClick={() => setOpen(true)} />
      <div className="max-w-7xl mx-auto flex min-h-[calc(100vh-4rem)]">
        <ZijMenu
          titel={t('werkplek.menu.titel')}
          ondertitel={t('werkplek.menu.ondertitel')}
          groepen={[{ items }]}
          mobielOpen={open}
          onMobielSluit={() => setOpen(false)}
        />
        <div className="flex-1 min-w-0">
          {titel && <PaginaKop titel={titel} ondertitel={ondertitel} acties={acties} className="px-4 sm:px-6 pt-6" />}
          {children}
        </div>
      </div>
    </div>
  );
};
